import { Injectable } from '@angular/core';
import { StorageHelperService } from './storage-helper.service';

/** The campaign attribution captured from a media-kit QR landing link. */
export interface UtmAttribution {
  utm_source: string;
  signature: string | null;
}

/**
 * Captures and persists the media-kit campaign attribution (`utm_source` +
 * `signature`) for the lifetime of the browser session.
 *
 * A user who lands via a media-kit QR link arrives with
 * `?utm_source=media_kit&signature=…`. Those params are dropped by the router on
 * the first in-app navigation, so we snapshot them once on app start and keep
 * them in `sessionStorage` (which clears only when the browser/tab is closed).
 * The persisted values are then attached to analytics for the whole session so
 * the visit remains attributable to the media-kit promotional tools.
 *
 * Behavior is deliberately:
 * - **media_kit-only** — only `utm_source=media_kit` is captured.
 * - **first-touch / sticky** — once stored, a later landing does not overwrite it.
 */
@Injectable({ providedIn: 'root' })
export class UtmAttributionService {
  private static readonly STORAGE_KEY = 'gs_utm_attribution';
  private static readonly TRACKED_UTM_SOURCE = 'media_kit';

  constructor(private storageHelper: StorageHelperService) {}

  /**
   * Reads the landing URL's query string and, if it carries
   * `utm_source=media_kit`, stores the attribution once for the session.
   * First-touch: an already-stored attribution is preserved untouched.
   *
   * @param search The query string to read. Defaults to the live location so
   *   callers (e.g. the app initializer) can call it with no arguments; tests
   *   can pass an explicit string.
   */
  captureFromUrl(search: string = window.location.search): void {
    // First-touch guard: never overwrite an attribution already set this session.
    if (this.storageHelper.hasItem(UtmAttributionService.STORAGE_KEY)) return;

    let utmSource: string | null;
    let signature: string | null;
    try {
      const params = new URLSearchParams(search);
      utmSource = params.get('utm_source');
      signature = params.get('signature');
    } catch {
      return;
    }

    if (utmSource !== UtmAttributionService.TRACKED_UTM_SOURCE) return;

    this.storageHelper.setItem<UtmAttribution>(UtmAttributionService.STORAGE_KEY, {
      utm_source: utmSource,
      signature: signature ?? null,
    });
  }

  /** Returns the stored session attribution, or `null` if none was captured. */
  getAttribution(): UtmAttribution | null {
    return this.storageHelper.getItem<UtmAttribution>(UtmAttributionService.STORAGE_KEY);
  }

  /** Removes the stored attribution (not tied to logout — only an explicit reset). */
  clear(): void {
    this.storageHelper.removeItem(UtmAttributionService.STORAGE_KEY);
  }
}
